import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../Temp/Sidebar/Sidebar";
import { useAuthorization } from "../hooks/useAuthorizations";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { useOutletContext, Navigate } from "react-router-dom";

// Lazy load components
const TheoryForm = lazy(() => import("./TheoryForm"));
const TheoryFormProgressBar = lazy(() => import("./TheoryFormProgressBar"));
const QuestionList = lazy(() => import("./QuestionList"));
const TheoryAIQuestionSection = lazy(() => import("./AIQuestionSection"));
const Dialogs = lazy(() => import("./Dialogs"));
const InstructionsDialog = lazy(() => import("./InstructionsDialog"));

// Default instructions
const defaultInstructions = [
  {
    heading: "Instructions",
    points: [
      "Read all questions carefully before answering.",
      "Ensure you have a stable internet connection.",
      "Do not refresh the page during the assessment.",
      "Use of calculators is not allowed unless specified.",
      "Answer all questions within the allotted time.",
      "Do not use any external resources or assistance.",
      "Submit your answers before closing the assessment window.",
      "Follow the format specified for each question.",
      "Check your answers before final submission.",
      "Contact the invigilator for any technical issues.",
    ],
  },
];

// Loading fallback
const LoadingFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
    <div>Loading...</div>
  </div>
);

// Main loading with sidebar
const MainLoadingComponent = () => (
  <div className="flex min-h-screen">
    {/* <div className="w-[95px] flex-shrink-0">
<Sidebar />
</div> */}
    <div className="flex-1 overflow-auto pl-6 pr-6 pt-6">
      <div className="bgImage" style={{ paddingLeft: "100px", marginLeft: "-100px" }}>
        <Box className="MainSectionTop" sx={{ pl: 3, pr: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: 'rgba(255, 255, 255, 0.8)', borderRadius: '8px', margin: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <img className="pd-loading-video" src="/images/breath1.gif" alt="Loading..." style={{ width: '120px', height: '120px', marginBottom: '20px' }} />
            </div>
          </div>
        </Box>
      </div>
    </div>
  </div>
);

export const CreateCourse = () => {
  const deleteQueueRef = useRef([]);
  // Add API call to queue
  const queueDeleteAction = (action) => {
    console.log("Queued delete action:", action);
    deleteQueueRef.current.push(action);   // ✅ CORRECT
  };
  const { type, examid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentExamId = examid?.trim?.() || "0";


  const { permissionLevel, loading: authLoading } = useAuthorization();
  const { examData } = location.state || {};
  const { mode } = location.state || {};

  // ── VIEW MODE: override permission level so all children become read-only ──
  const effectivePermissionLevel = mode === 'view' ? 1 : permissionLevel;

  const searchParams = new URLSearchParams(location.search);
  const queryExamType = searchParams.get('type');
  const [tempOptionUrls, setTempOptionUrls] = useState({});
  const [tempQuestionUrls, setTempQuestionUrls] = useState({});

  const [isRelative, setIsRelative] = useState(0);
  const [isenable, setIsEnable] = useState(0);
  const [editSectionsLoaded, setEditSectionsLoaded] = useState(false);

  const canSeeActions = effectivePermissionLevel > 0;
  const canUseActions = effectivePermissionLevel === 2;
  const isViewOnly = effectivePermissionLevel === 1;

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  const [courseType, setCourseType] = useState("objective");
  const [difficulty, setDifficulty] = useState("");
  const [category, setCategory] = useState("");
  const [editaimaterial, setEditMaterial] = useState("");
  const [percentage, setPercentage] = useState("");
  const [duration, setDuration] = useState("");
  const [courseTitle, setCourseTitle] = useState("");
  const [courseDesc, setCourseDesc] = useState("");
  const [vivaFile, setVivaFile] = useState(null);
  const [categories, setCategories] = useState(null);
  const [difficulties, setDifficulties] = useState(null);
  const [questionTypes, setQuestionTypes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);
  const [qaData, setQaData] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewFileOpen, setViewFileOpen] = useState(false);
  const [viewFile, setViewFile] = useState(null);
  const [aiSectionCollapsed, setAiSectionCollapsed] = useState(false);
  // const [currentPage, setCurrentPage] = useState(1);
  const [sectionPages, setSectionPages] = useState({});

  const [reviewRequired, setReviewRequired] = useState(false);
  const [reviewWithAnswers, setReviewWithAnswers] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [exam, setExam] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [vivaQaData, setVivaQaData] = useState([]);
  const [vivaQuestionCount, setVivaQuestionCount] = useState("");
  const [vivaMaterialFile, setVivaMaterialFile] = useState(null);
  const [vivaQuestionsGenerated, setVivaQuestionsGenerated] = useState(false);
  const [vivaError, setVivaError] = useState(null);
  const [assessmentTypeId, setAssessmentTypeId] = useState(""); // ExamGenreId
  const [examGenres, setExamGenres] = useState([]);
  const [loadingExamGenres, setLoadingExamGenres] = useState(false);
  const [noOfQuestions, setNoOfQuestions] = useState("");
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  const [goClicked, setGoClicked] = useState(false);
  // Near other dialog states
  const [sectionDeleteConfirmOpen, setSectionDeleteConfirmOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);


  const [aiMaterialFile, setAiMaterialFile] = useState(null);
  const [aiDomain, setAiDomain] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiSubtopic, setAiSubtopic] = useState("");
  const [sectionError, setSectionError] = useState("");
  const [sectionErrors, setSectionErrors] = useState({});

  // Sections state
  const [sections, setSections] = useState([]);

  // Per-section AI data (for AI sections)
  const [aiSectionData, setAiSectionData] = useState({}); // key: sectionId, value: { domain, topic, subtopic, questionTypes, questionCount, materialFile }
  const [aiQuestionsGenerated, setAiQuestionsGenerated] = useState({}); // key: sectionId, value: boolean

  // Per-section questions (for both manual and AI sections)
  const [sectionQuestions, setSectionQuestions] = useState({}); // key: sectionId, value: array of questions

  // Collapse state per section
  const [collapsedSections, setCollapsedSections] = useState({});

  const examStatus = useMemo(() => exam?.status || 0, [exam]);
  const isPublished = examStatus === 1;
  // const isViewOnly = permissionLevel === 1;

  const [saveSummary, setSaveSummary] = useState(null);
  const [lastSavedStatus, setLastSavedStatus] = useState(null);
  const { permissionSet, permissionsLoading } = useOutletContext();


  const canAccessAssessment = permissionSet.has(5) || permissionSet.has(6);


  const [examPurpose, setExamPurpose] = useState("");
  const [examPurposes, setExamPurposes] = useState([]);
  const [otherPurpose, setOtherPurpose] = useState("");
  const [purposeChangeDialogOpen, setPurposeChangeDialogOpen] = useState(false);
  const [pendingPurpose, setPendingPurpose] = useState("");
  const [maxAttempts, setMaxAttempts] = useState(1);


  const handlePurposeChangeConfirm = async () => {
    try {
      setIsSaving(true);

      let questionIds = [];

      // Main Questions
      Object.values(qaData).forEach((questions) => {
        if (!Array.isArray(questions)) return;

        questions.forEach((q) => {
          if (q.questionID) {
            questionIds.push(q.questionID);
          }
        });
      });

      // Section Questions
      Object.values(sectionQuestions).forEach((questions) => {
        if (!Array.isArray(questions)) return;

        questions.forEach((q) => {
          if (q.questionID) {
            questionIds.push(q.questionID);
          }
        });
      });

      console.log("Deleting Question IDs:", questionIds);

      if (questionIds.length > 0) {
        await axios.put("/manager/delete_question/", {
          question_id: questionIds,
        });
      }

      // Clear UI
      setQaData({});
      setSectionQuestions({});
      setSections([]);
      setAiSectionData({});
      setAiQuestionsGenerated({});
      setCollapsedSections({});
      setSectionPages({});

      // Apply new purpose
      setExamPurpose(pendingPurpose);

      if (pendingPurpose !== "Others") {
        setOtherPurpose("");
      }

      setPurposeChangeDialogOpen(false);
      setPendingPurpose("");

    } catch (err) {
      console.error(err);
      setError("Failed to delete existing questions.");
    } finally {
      setIsSaving(false);
    }
  };




  const examTypeId = useMemo(() => {
    return courseType === "competency" ? 2 : 1;
  }, [courseType]);



  useEffect(() => {
    const fetchExamPurposes = async () => {
      try {
        const res = await axios.get("/manager/get-exam-purpose/");

        console.log(res.data);

        setExamPurposes(res.data);

      } catch (err) {
        console.error("Failed to fetch exam purposes", err);
      }
    };

    fetchExamPurposes();
  }, []);


  useEffect(() => {
    console.log("Parent examPurpose =", examPurpose);
    console.log("Parent otherPurpose =", otherPurpose);
  }, [examPurpose, otherPurpose]);
  const isObjective = examTypeId === 1;
  const isViva = examTypeId === 2;

  const canSaveAsDraft = useMemo(() => {
    // At minimum — title + some description + pass % + duration + num questions
    const hasTitle =
      courseTitle?.trim().length > 0;

    return hasTitle;
  }, [
    courseTitle,
  ]);




  useEffect(() => {
    // Reset everything when switching exam type
    resetCourseForm();

    // Optional: clear sections also explicitly
    setSections([]);
    setSectionQuestions({});
    setAiSectionData({});
    setAiQuestionsGenerated({});
    setCollapsedSections({});
    setQaData({});
    setEditSectionsLoaded(false);
  }, [type]);   // or [courseType]



  // Helper to update AI data for a section
  const updateAiSectionData = (sectionId, field, value) => {
    setAiSectionData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };

  // Helper to get AI data for a section (with defaults)
  const getAiDataForSection = (sectionId) => {
    return aiSectionData[sectionId] || {
      domain: "",
      topic: "",
      subtopic: "",
      questionTypes: [],
      questionCount: 10,
      materialFile: null,
      materialPath: ""
    };
  };

  // All preview questions (main + sections)
  const allPreviewQuestions = useMemo(() => {
    const mainQuestions = Object.entries(qaData).flatMap(([diff, questions]) =>
      Array.isArray(questions) ? questions.filter(q => q?.isActive !== false).map(q => ({ ...q, difficulty: diff, sectionId: 'main' })) : []
    );
    const extraQuestions = Object.entries(sectionQuestions).flatMap(([secId, qs]) =>
      Array.isArray(qs) ? qs.map(q => {
        const section = sections.find(s => s.id === secId);
        return { ...q, difficulty: section?.difficulty || '?', sectionId: secId };
      }) : []
    );
    return [...mainQuestions, ...extraQuestions];
  }, [qaData, sectionQuestions, sections]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (!reviewOpen) return;

    const optionUrls = {};
    const questionUrls = {};

    allPreviewQuestions.forEach((q, qIdx) => {
      // Handle option images
      if (q.optionFiles) {
        q.optionFiles.forEach((file, optIdx) => {
          if (file instanceof File) {
            const key = `${qIdx}-${optIdx}`;
            optionUrls[key] = URL.createObjectURL(file);
          }
        });
      }
      // Handle question image
      if (q.questionFile instanceof File) {
        questionUrls[qIdx] = URL.createObjectURL(q.questionFile);
      }
    });

    setTempOptionUrls(optionUrls);
    setTempQuestionUrls(questionUrls);

    return () => {
      Object.values(optionUrls).forEach(url => URL.revokeObjectURL(url));
      Object.values(questionUrls).forEach(url => URL.revokeObjectURL(url));
      setTempOptionUrls({});
      setTempQuestionUrls({});
    };
  }, [reviewOpen, allPreviewQuestions]);

  const hasQuestionsDisplayed = useMemo(() => {
    // Check main
    if (Object.values(qaData).some(arr => Array.isArray(arr) && arr.some(q => q?.isActive !== false && q.question?.trim()))) return true;
    // Check sections
    return Object.values(sectionQuestions).some(arr => Array.isArray(arr) && arr.some(q => q?.isActive !== false && q.question?.trim()));
  }, [qaData, sectionQuestions]);

  // Fetch exam genres
  useEffect(() => {
    const fetchExamGenres = async () => {
      try {
        setLoadingExamGenres(true);
        const res = await axios.get("/manager/exam-genres/");
        if (res.data?.success) setExamGenres(res.data.data);
      } catch (err) {
        console.error("Failed to load exam genres", err);
      } finally {
        setLoadingExamGenres(false);
      }
    };
    fetchExamGenres();
  }, []);

  // Set course type from URL / state
  useEffect(() => {
    if (location.state?.examType) setCourseType(location.state.examType);
    else if (queryExamType) setCourseType(queryExamType);
    else if (type === "2") setCourseType("competency");
    else if (type === "1") setCourseType("objective");
    else setCourseType("objective");
  }, [type, queryExamType, location.state]);

  // Transform questions from API
  const transformQuestionsData = useCallback((questions, sectionId) => {
    return questions.map(q => {
      const qType = (q.question_type_id || q.QuestionTypeID || q.type || "").toString();

      let correctAnswerValue;

      if (qType === "4" || qType === "3") {
        // Viva / descriptive / fill-in → keep answer as plain string
        correctAnswerValue = q.answers || q.answer || q.Expected_Answer || "";
      } else if (qType === "2") {
        // MSQ → array of indices
        correctAnswerValue = (q.answers || "")
          .split(",")
          .map(letter => "ABCD".indexOf(letter.trim()))
          .filter(idx => idx !== -1);
      } else {
        // MCQ (type 1) → single index
        correctAnswerValue = "ABCD".indexOf(q.answers || "");
      }
      return {
        id: Date.now() + Math.random(),   // ✅ ADD
        sectionId: sectionId,
        questionID: q.Id,
        question: q.question_description || q.QuestionDescription,
        type: (q.question_type_id || q.QuestionTypeID)?.toString(),
        topic: q.topic || q.Topic || "",
        options: [q.a || "", q.b || "", q.c || "", q.d || ""],
        correctAnswer: correctAnswerValue,
        questionFile: null,
        optionFiles: [null, null, null, null],
        questionImagePath: q.question_image_path || q.QuestionImagePath,
        optionPaths: [q.a_path || q.APath, q.b_path || q.BPath, q.c_path || q.CPath, q.d_path || q.DPath],
        isActive: q.IsActive !== false,
        isMandatory: (q.IsMandatory === 1 || q.ismandatory === 1),
      };
    });
  }, []);


  useEffect(() => {
    if (!exam || examPurposes.length === 0) return;

    const purposeValue = exam.exampurpose || "";

    const exists = examPurposes.some(
      p => p.purpose === purposeValue
    );

    if (exists) {
      setExamPurpose(purposeValue);
      setOtherPurpose("");
    } else {
      setExamPurpose("Others");
      setOtherPurpose(purposeValue);
    }
  }, [exam, examPurposes]);


  useEffect(() => {
    const loadAllInitialData = async () => {
      resetCourseForm();
      if (!canSeeActions) {
        setLoading(false);
        setInitialLoadComplete(true);
        setFormChanged(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [categoryRes, difficultyRes] = await Promise.all([
          axios.get(`/manager/category/`, { params: { plantid: 1 } }),
          axios.get(`/manager/difficulty/`, { params: { plantid: 1 } })
        ]);

        setCategories(categoryRes.data.data.filter(cat => cat.Id !== 2));
        setDifficulties(difficultyRes.data.data);


        if (examData) {
          setCourseTitle(examData.Title || "");
          setCourseDesc(examData.Description || "");
          setDifficulty(examData.DifficultyID?.toString() || "");
          setCategory(examData.CategoryID?.toString() || "");
        }

        // ====================== EDIT MODE ======================
        if (examid !== "0") {
          const [examRes, questionTypesRes] = await Promise.all([
            axios.get(`/manager/theory_exam/`, { params: { exam_id: examid } }),
            axios.get(`/manager/question_type/`, { params: { plantid: 1 } })
          ]);

          const { exam: examDataFromApi, questions } = examRes.data;
          setExam(examDataFromApi);
          console.log("API IsRelative:", examDataFromApi.is_relative);

          // Fill form fields
          if (!examData) {
            setCourseTitle(examDataFromApi.title || examDataFromApi.Title || "");
            //             const purposeValue = examDataFromApi.exampurpose || "";


            // const exists = examPurposes.some(
            //   p =>
            //     p.purpose?.trim().toLowerCase() ===
            //     purposeValue?.trim().toLowerCase()
            // );
            //             if (exists) {
            //               setExamPurpose(purposeValue);
            //             } else {
            //               setExamPurpose("Others");
            //               setOtherPurpose(purposeValue);
            //             }
            setExam(examDataFromApi);
            setAssessmentTypeId(String(examDataFromApi.examgenreid || examDataFromApi.ExamGenreId || ""));
            setCourseDesc(examDataFromApi.description || examDataFromApi.Description || "");
            setPercentage(examDataFromApi.pass_percent || examDataFromApi.PassPercent || "");
            setDuration(examDataFromApi.duration_min || examDataFromApi["Duration(min)"] || "");
            setDifficulty(examDataFromApi.difficulty_id?.toString() || examDataFromApi.DifficultyID?.toString() || "");
            setCategory(examDataFromApi.category_id?.toString() || examDataFromApi.CategoryID?.toString() || "");
            // setIsRelative(
            //   examDataFromApi.is_relative === 1 ||
            //     examDataFromApi.IsRelative === 1
            //     ? 1
            //     : 0
            // );
            setMaxAttempts(
              Number(
                examDataFromApi.no_of_attempts ??
                examDataFromApi.NoOfAttempts ??
                1
              )
            );
            const relativeValue =
              examDataFromApi.is_relative ??
              examDataFromApi.IsRelative ??
              0;

            setIsRelative(Number(relativeValue) === 1 ? 1 : 0);
          }
          const enableValue =
            examDataFromApi.isenable !== undefined &&
              examDataFromApi.isenable !== null
              ? examDataFromApi.isenable
              : examDataFromApi.IsEnable !== undefined &&
                examDataFromApi.IsEnable !== null
                ? examDataFromApi.IsEnable
                : 0;

          console.log("isenable from API:", examDataFromApi.isenable);
          console.log("IsEnable from API:", examDataFromApi.IsEnable);
          console.log("Final enableValue:", enableValue);

          setIsEnable(Number(enableValue) === 1 ? 1 : 0);

          setEditMaterial(examDataFromApi.file_name || examDataFromApi.MaterialPath || "");
          setReviewRequired(examDataFromApi.review_required === 1);
          setReviewWithAnswers(examDataFromApi.review_with_answers === 1);
          setInstructions(examDataFromApi.instructions || JSON.stringify(defaultInstructions));

          // Group questions by difficulty (for main section)
          // ====================== MULTI-SECTION LOADING FOR EDIT (NEW) ======================
          const comboToSection = new Map();
          const comboToQs = new Map();
          const comboToAiInfo = new Map();

          questions.forEach(q => {
            const catId = String(q.CategoryID || q.category_id || "");
            const diffId = String(q.DifficultyID || q.difficulty_id || "");
            const genreId = String(q.ExamGenreId || q.exam_genre_id || "");

            if (!catId || !diffId) return;

            const key = `${catId}-${diffId}-${genreId}`;

            if (!comboToSection.has(key)) {
              const secId = `edit-${key.replace(/-/g, "")}-${Math.random().toString(36).substring(2, 10)}`;
              comboToSection.set(key, {
                id: secId,
                genre: genreId || assessmentTypeId || "",
                difficulty: diffId,
                category: catId,
                isEditing: false,
              });
              comboToQs.set(key, []);
              if (catId === "3") {
                comboToAiInfo.set(key, {
                  domain: q.Domain || q.domain || "",
                  topic: q.AiTopicForQuestion || q.ai_topic_for_question || "",
                  subtopic: q.SubTopic || q.sub_topic || "",
                  questionTypes: [],
                  questionCount: 0,
                  materialFile: null,
                  materialPath: q.MaterialPath || q.material_path || "",
                });
              }
            }

            // const transQ = transformQuestionsData([q])[0];
            const secId = comboToSection.get(key).id;  // or wherever section is available
            const transQ = transformQuestionsData([q], secId)[0];
            comboToQs.get(key).push(transQ);

            if (catId === "3") {
              const aiInfo = comboToAiInfo.get(key);
              const qTypeStr = transQ.type;
              if (!aiInfo.questionTypes.includes(qTypeStr)) {
                aiInfo.questionTypes.push(qTypeStr);
              }
              aiInfo.questionCount = comboToQs.get(key).length;
              if (!aiInfo.materialPath) {
                aiInfo.materialPath = q.MaterialPath || q.material_path || "";
              }
            }
          });

          // After processing comboToSection
          const loadedSections = Array.from(comboToSection.values()).map((sec, index) => ({
            ...sec,
            name: sec.name || generateSectionName(index),  // Assign A, B, C if no name exists
          }));

          setSections(loadedSections);
          if (loadedSections.length > 0) {
            setGoClicked(true);
            setEditSectionsLoaded(true);
          }

          const loadedSectionQuestions = {};
          Array.from(comboToQs.keys()).forEach((key) => {
            const secId = comboToSection.get(key).id;
            loadedSectionQuestions[secId] = comboToQs.get(key);
          });
          setSectionQuestions(loadedSectionQuestions);

          const loadedAiSectionData = {};
          const loadedAiQuestionsGenerated = {};
          Array.from(comboToAiInfo.keys()).forEach((key) => {
            if (comboToAiInfo.get(key)) {
              const secId = comboToSection.get(key).id;
              loadedAiSectionData[secId] = comboToAiInfo.get(key);
              loadedAiQuestionsGenerated[secId] = true;
            }
          });
          setAiSectionData(loadedAiSectionData);
          setAiQuestionsGenerated(loadedAiQuestionsGenerated);

          // Set number of questions & fallback assessment type
          setNoOfQuestions(String(examDataFromApi.QuestionsPerUser || questions.length || ""));
          if (!assessmentTypeId && loadedSections.length > 0) {
            setAssessmentTypeId(loadedSections[0].genre);
          }

          setQuestionTypes(
            examTypeId === 2
              ? questionTypesRes.data.data.filter(qt => qt.ID === 4)
              : questionTypesRes.data.data.filter(qt => qt.ID !== 4)
          );

        }
        // ====================== NEW EXAM ======================
        else {
          setInstructions(JSON.stringify(defaultInstructions));
          const qtRes = await axios.get(`/manager/question_type/`, { params: { plantid: 1 } });
          setQuestionTypes(
            examTypeId === 2
              ? qtRes.data.data.filter(qt => qt.ID === 4)
              : qtRes.data.data.filter(qt => qt.ID !== 4)
          );
        }
      } catch (err) {
        console.error("Failed to load exam data:", err);
        setError("Failed to load exam data. Please try again.");
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
        setFormChanged(false);
      }
    };

    loadAllInitialData();
  }, [examid, courseType, transformQuestionsData, canSeeActions, examData]);


  const [formChanged, setFormChanged] = useState(false);
  useEffect(() => {
    sessionStorage.setItem(
      "hasUnsavedChanges",
      formChanged ? "true" : "false"
    );
  }, [formChanged]);


  const handleInstructionsSave = useCallback((instructionsData) => {
    if (canUseActions) setInstructions(instructionsData);
  }, [canUseActions]);

  const handleOpenInstructions = useCallback(() => {
    if (canUseActions) setInstructionsDialogOpen(true);
  }, [canUseActions]);

  // const isFormFilled = useMemo(() => !!(
  //   courseTitle?.trim() && courseDesc?.trim() && percentage && duration && noOfQuestions && parseInt(noOfQuestions, 10) > 0
  // ), [courseTitle, courseDesc, percentage, duration, noOfQuestions]);


  const isFormFilled = useMemo(() => {
    const purposeValid =
      examPurpose &&
      (
        examPurpose !== "Others" ||
        (otherPurpose && otherPurpose.trim())
      );

    return !!(
      courseTitle?.trim() &&
      purposeValid &&
      courseDesc?.trim() &&
      percentage &&
      duration &&
      noOfQuestions &&
      parseInt(noOfQuestions, 10) > 0
    );
  }, [
    courseTitle,
    examPurpose,
    otherPurpose,
    courseDesc,
    percentage,
    duration,
    noOfQuestions
  ]);

  useEffect(() => {
    setGoClicked(false);
  }, [
    courseTitle,
    courseDesc,
    percentage,
    duration,
    noOfQuestions
  ]);

  const resetCourseForm = useCallback(() => {
    if (!canUseActions) return;
    setCourseTitle("");
    setAssessmentTypeId("");
    setCourseDesc("");
    setPercentage("");
    setDuration("");
    setDifficulty("");
    setCategory("");
    setQaData({});
    setConfirmOpen(false);
    setConfirmationType("");
    setSuccessOpen(false);
    setVivaFile(null);
    setEditMaterial("");
    setAiSectionCollapsed(false);
    setViewFileOpen(false);
    setViewFile(null);
    setIsRelative(0);
    setIsEnable(0);
    setReviewRequired(false);
    setReviewWithAnswers(false);
    setInstructions(JSON.stringify(defaultInstructions));
    setInstructionsDialogOpen(false);
    setExam(null);
    setVivaQaData([]);
    setVivaQuestionCount("");
    setVivaMaterialFile(null);
    setVivaQuestionsGenerated(false);
    setVivaError(null);
    setIsGeneratingQuestions(false);
    setReviewOpen(false);
    setShowConfigPanel(false);
    setNoOfQuestions("");
    setSections([]);
    setAiSectionData({});
    setAiQuestionsGenerated({});
    setSectionQuestions({});
    setCollapsedSections({});
    setExamPurpose("");
    setOtherPurpose("");
    setPendingPurpose("");
    setPurposeChangeDialogOpen(false);

    setQaData({});
    setSections([]);
    setSectionQuestions({});
    setEditSectionsLoaded(false);
    setMaxAttempts(1);
  }, [canUseActions]);


  const generateSectionName = (index) => {
    return String.fromCharCode(65 + index); // 65 = 'A', 66 = 'B', etc.
  };


  const handleAddSection = () => {
    const id = Date.now().toString();
    const newSectionIndex = sections.length; // current length = next index
    const newSection = {
      id,
      name: generateSectionName(newSectionIndex), // ← Add name: "A", "B", etc.
      genre: "",
      difficulty: "",
      category: "",
      isEditing: true,
    };
    setSections(prev => [...prev, newSection]);
    setSectionPages(prev => ({
      ...prev,
      [id]: 1
    }));
  };


  const updateSection = (sectionId, updates) => {
    setSections(prev => {
      const updated = prev.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      );

      const current = updated.find(s => s.id === sectionId);
      if (!current) return updated;

      // Only check when all 3 selected
      if (current.genre && current.category && current.difficulty) {
        const duplicate = updated.some(s =>
          s.id !== sectionId &&
          String(s.genre) === String(current.genre) &&
          String(s.category) === String(current.category) &&
          String(s.difficulty) === String(current.difficulty)
        );

        // if (duplicate) {
        //   // 🔴 Set error only for this section
        //   setSectionErrors(prevErr => ({
        //     ...prevErr,
        //     [sectionId]: "This combination already exists."
        //   }));

        //   // 🔥 CLEAR dropdowns
        //   return updated.map(s =>
        //     s.id === sectionId
        //       ? {
        //         ...s,
        //         genre: "",
        //         category: "",
        //         difficulty: "",
        //         isEditing: true
        //       }
        //       : s
        //   );
        // }

        // ✅ Valid → clear error
        setSectionErrors(prevErr => {
          const copy = { ...prevErr };
          delete copy[sectionId];
          return copy;
        });

        return updated.map(s =>
          s.id === sectionId ? { ...s, isEditing: false } : s
        );
      }

      return updated;
    });
  };


  const removeSection = (sectionId) => {
    try {
      const questionsInSection = sectionQuestions[sectionId] || [];

      // ✅ Get DB question IDs
      const dbQuestionIds = questionsInSection
        .filter(q => q.questionID)
        .map(q => q.questionID);

      // ✅ Queue instead of API call
      if (dbQuestionIds.length > 0) {
        queueDeleteAction(() =>
          axios.put("/manager/delete_question/", {
            question_id: dbQuestionIds
          })
        );
      }

      // ✅ Remove from UI immediately (optimistic)
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setSectionPages(prev => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });
      setSectionQuestions(prev => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });
      setAiSectionData(prev => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });
      setAiQuestionsGenerated(prev => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });
      setCollapsedSections(prev => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });

    } catch (err) {
      console.error("Queueing delete failed:", err);
      alert("Something went wrong. Please try again.");
    }
  };


  const toggleCollapse = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const finishSectionEditing = (sectionId) => {
    setSections(prev =>
      prev.map(s => {
        if (s.id === sectionId) {
          // Required fields check
          if (!s.difficulty || !s.category || !s.genre) {
            setSectionError("Please fill all fields before saving this section.");
            return s;
          }

          // Duplicate category + difficulty check (using string comparison)
          const duplicate = prev.some(other =>
            other.id !== sectionId &&
            String(other.difficulty) === String(s.difficulty) &&
            String(other.category) === String(s.category)
          );

          if (duplicate) {
            setSectionErrors(
              "This Category + Difficulty combination already exists. Please choose a different one."
            );
            return s; // do NOT save
          }

          // All good
          setSectionErrors("");
          return { ...s, isEditing: false };
        }
        return s;
      })
    );
  };

  const editSection = (sectionId) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, isEditing: true } : s));
  };

  const difficultyLabelMap = { "1": "Beginner", "2": "Intermediate", "3": "Expert" };

  const handleSaveDraft = () => {
    if (!canSaveAsDraft) {
      return;
    }
    console.warn("User explicitly clicked SAVE AS DRAFT");
    setConfirmationType("draft");
    setConfirmOpen(true);
  };

  const handleSaveExam = () => {
    console.warn("User explicitly clicked SAVE ASSESSMENT (publish)");
    setError(null);
    // 🔥 NEW: Validate topics for all questions
    const missingTopicQuestions = [];

    // Check main questions
    Object.entries(qaData).forEach(([diff, questions]) => {
      if (!Array.isArray(questions)) return;
      questions.forEach((question, idx) => {
        if (question.isActive !== false && (!question.topic || question.topic.trim() === "")) {
          missingTopicQuestions.push(`Main - Difficulty ${difficultyLabelMap[diff] || diff} - Q${idx + 1}`);
        }
      });
    });

    // Check section questions
    sections.forEach(section => {
      const questions = sectionQuestions[section.id] || [];
      questions.forEach((question, idx) => {
        if (question.isActive !== false && (!question.topic || question.topic.trim() === "")) {
          const sectionName = section.name || `Section ${section.id.slice(0, 8)}`;
          missingTopicQuestions.push(`${sectionName} - ${difficultyLabelMap[section.difficulty] || section.difficulty} - Q${idx + 1}`);
        }
      });
    });

    // Show error if any questions are missing topics
    if (missingTopicQuestions.length > 0) {
      setError(`⚠️ Please add topic for the following question(s):\n${missingTopicQuestions.join(', ')}`);
      setTimeout(() => setError(null), 7000);
      return;
    }
    if (Object.keys(sectionErrors).length > 0) {
      setError("Please fix section errors before saving.");
      return;
    }
    const shortAnswerQuestions = [];

    // Main questions
    Object.entries(qaData).forEach(([diff, questions]) => {
      if (!Array.isArray(questions)) return;
      questions.forEach((question, idx) => {
        const isExpectedAnswerType = isViva || question.type === "4";
        if (
          question.isActive !== false &&
          isExpectedAnswerType &&
          (question.correctAnswer || "").trim().length < 70
        ) {
          shortAnswerQuestions.push(`Q${idx + 1}`);
        }
      });
    });

    // Section questions
    sections.forEach(section => {
      const questions = sectionQuestions[section.id] || [];
      questions.forEach((question, idx) => {
        const isExpectedAnswerType = isViva || question.type === "4";
        if (
          question.isActive !== false &&
          isExpectedAnswerType &&
          (question.correctAnswer || "").trim().length < 70
        ) {
          shortAnswerQuestions.push(`Q${idx + 1}`);
        }
      });
    });

    if (shortAnswerQuestions.length > 0) {
      setError(`Expected Answer must be at least 70 characters for the following question(s):\n${shortAnswerQuestions.join(', ')}`);
      setTimeout(() => setError(null), 7000);
      return;
    }


    // const seen = new Set();

    // for (const section of sections) {
    //   if (!section.genre || !section.category || !section.difficulty) continue;

    //   const key = `${section.genre}-${section.category}-${section.difficulty}`;

    //   if (seen.has(key)) {
    //     setError("Duplicate section combination found.");
    //     return;
    //   }

    //   seen.add(key);
    // }
    const invalidQuestions = [];

    const questions = buildQuestionsPayload();

    const missingAnswers = questions
      .map((q, i) => (q.question_type_id === 4 && !q.answers?.trim()) ? i : -1)
      .filter(i => i >= 0);

    if (missingAnswers.length > 0) {
      setError(
        `Answer is required for the following competency question(s): ${missingAnswers.map(i => `Q${i + 1}`).join(", ")}`
      );
      return;
    }

    // Validate main questions
    Object.entries(qaData).forEach(([diff, questions]) => {
      if (!Array.isArray(questions)) return;
      questions.forEach((question, idx) => {
        if (question.isActive !== false) {
          if (
            question.type === "1" &&
            (question.correctAnswer === null || question.correctAnswer === undefined)
          ) {
            invalidQuestions.push(`Main - Difficulty ${difficultyLabelMap[diff] || diff} - Q${idx + 1}`);
          }
          if (
            question.type === "2" &&
            (!Array.isArray(question.correctAnswer) || question.correctAnswer.length < 2)
          ) {
            invalidQuestions.push(`Main - Difficulty ${difficultyLabelMap[diff] || diff} - Q${idx + 1}`);
          }
        }
      });
    });

    // Validate each section's questions
    sections.forEach(section => {
      const questions = sectionQuestions[section.id] || [];
      questions.forEach((question, idx) => {
        if (question.isActive !== false) {
          if (
            question.type === "1" &&
            (question.correctAnswer === null || question.correctAnswer === undefined)
          ) {
            invalidQuestions.push(`Section ${section.id} - ${difficultyLabelMap[section.difficulty] || section.difficulty} - Q${idx + 1}`);
          }
          if (
            question.type === "2" &&
            (!Array.isArray(question.correctAnswer) || question.correctAnswer.length < 2)
          ) {
            invalidQuestions.push(`Section ${section.id} - ${difficultyLabelMap[section.difficulty] || section.difficulty} - Q${idx + 1}`);
          }
        }
      });
    });

    if (invalidQuestions.length > 0) {
      setError(`Please select correct option(s) for the following question(s):\n${invalidQuestions.join(', ')}`);
      setTimeout(() => setError(null), 7000);
      return;
    }

    setConfirmationType("save-summary");
    setConfirmOpen(true);
  };



  const handleClearForm = () => {
    setConfirmationType("clear");
    setConfirmOpen(true);
  };

  const handleConfirmClear = useCallback(async () => {
    if (!canUseActions) return;
    try {
      setIsSaving(true);
      await axios.put("/manager/deactivate_questions/", { exam_id: examid, difficulty: difficulty });
      resetCourseForm();
    } catch (err) {
      console.error("Failed to deactivate questions", err);
      setError("Failed to clear questions. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [canUseActions, resetCourseForm, examid, difficulty]);

  const handleSuccessClose = useCallback((type) => {
    setSuccessOpen(false);
    if (type === "clear") {
      if (canUseActions) resetCourseForm();
      return;
    }
    if (canUseActions) resetCourseForm();
    navigate("/managers/exams-list",{
   state: { statusFilter: lastSavedStatus === 1 ? "Published" : "Draft" }
  });
}, [resetCourseForm, navigate, canUseActions, lastSavedStatus]);



  const buildQuestionsPayload = () => {
    let allQuestions = [];

    // ────────────────────────────────
    //  MAIN QUESTIONS (single difficulty & category)
    // ────────────────────────────────
    Object.entries(qaData).forEach(([difficultyStr, questions]) => {
      if (!Array.isArray(questions)) return;

      questions.forEach(q => {
        if (q?.isActive === false) return;

        const isFillInBlank = String(q.type) === "3";

        allQuestions.push({
          question_id: q.questionID || null,   // include questionID for existing questions (edit mode)
          question_description: q.question?.trim() || "",
          question_type_id: isViva ? 4 : Number(q.type),
          answers: (q.type === "4" || examTypeId === 2)
            ? String(q.correctAnswer || "").trim()
            : q.type === "3"
              ? String(q.correctAnswer || "").trim()
              : Array.isArray(q.correctAnswer)
                ? q.correctAnswer.map(idx => "ABCD"[Number(idx)]).filter(Boolean).join(",")
                : "ABCD"[Number(q.correctAnswer)] || "",




          // ── Correct values for MAIN questions ───────────────
          category_id: Number(category) || 0,   // main category
          difficulty_id: Number(difficultyStr) || 0,   // from qaData key
          exam_genre_id: Number(assessmentTypeId) || 0,

          marks: 0,   // or 5 — depending on your logic
          is_active: true,
          status: 1,
          comments: "",
          a: q.options?.[0] || "",
          b: q.options?.[1] || "",
          c: q.options?.[2] || "",
          d: q.options?.[3] || "",
          is_blank: isFillInBlank ? 1 : 0,
          duration: 60,
          ismandatory: q.isMandatory ? 1 : 0,
          topic: q.topic || "",
          domain: "",
          sub_topic: "",
          ai_topic_for_question: "",
          question_image_path: "",
          material_path: "",


          question_image_path: q.questionFile ? "" : (q.questionImagePath || ""),
          material_path: q.materialFile ? "" : (q.materialPath || ""),

          a_image_path: q.optionFiles?.[0] ? "" : (q.optionImagePaths?.[0] || ""),
          b_image_path: q.optionFiles?.[1] ? "" : (q.optionImagePaths?.[1] || ""),
          c_image_path: q.optionFiles?.[2] ? "" : (q.optionImagePaths?.[2] || ""),
          d_image_path: q.optionFiles?.[3] ? "" : (q.optionImagePaths?.[3] || ""),

          _files: {
            question_image: q.questionFile,
            a_image: q.optionFiles?.[0],
            b_image: q.optionFiles?.[1],
            c_image: q.optionFiles?.[2],
            d_image: q.optionFiles?.[3],
            material_file: q.materialFile,
          }
        });
      });
    });

    // ────────────────────────────────
    //  SECTION QUESTIONS (use section-specific values!)
    // ────────────────────────────────
    Object.entries(sectionQuestions).forEach(([sectionId, questions]) => {
      const section = sections.find(s => s.id === sectionId);
      if (!section) return;

      const aiData = getAiDataForSection(sectionId);

      questions.forEach(q => {
        if (q?.isActive === false) return;

        const isFillInBlank = String(q.type) === "3";

        allQuestions.push({
          question_id: q.questionID || null,
          question_description: q.question?.trim() || "",
          question_type_id: Number(q.type),
          answers: (q.type === "4" || examTypeId === 2)
            ? String(q.correctAnswer || "").trim()
            : q.type === "3"
              ? String(q.correctAnswer || "").trim()
              : Array.isArray(q.correctAnswer)
                ? q.correctAnswer.map(idx => "ABCD"[Number(idx)]).filter(Boolean).join(",")
                : "ABCD"[Number(q.correctAnswer)] || "",


          // ── Correct values — use SECTION data! ───────────────
          category_id: Number(section.category) || 0,
          difficulty_id: Number(section.difficulty) || 0,
          exam_genre_id: Number(section.genre) || 0,

          marks: 5,
          is_active: true,
          status: 1,
          comments: "",
          a: q.options?.[0] || "",
          b: q.options?.[1] || "",
          c: q.options?.[2] || "",
          d: q.options?.[3] || "",
          is_blank: isFillInBlank ? 1 : 0,
          duration: 60,
          ismandatory: q.isMandatory ? 1 : 0,
          topic: q.topic || "",
          domain: aiData.domain || "",                            // from AI section config
          sub_topic: aiData.subtopic || "",                       // note: your state uses "subtopic"
          ai_topic_for_question: aiData.topic || "",
          question_image_path: "",
          material_path: "",

          question_image_path: q.questionFile ? "" : (q.questionImagePath || ""),
          material_path: q.materialFile ? "" : (q.materialPath || ""),

          a_image_path: q.optionFiles?.[0] ? "" : (q.optionImagePaths?.[0] || ""),
          b_image_path: q.optionFiles?.[1] ? "" : (q.optionImagePaths?.[1] || ""),
          c_image_path: q.optionFiles?.[2] ? "" : (q.optionImagePaths?.[2] || ""),
          d_image_path: q.optionFiles?.[3] ? "" : (q.optionImagePaths?.[3] || ""),

          _files: {
            question_image: q.questionFile,
            a_image: q.optionFiles?.[0],
            b_image: q.optionFiles?.[1],
            c_image: q.optionFiles?.[2],
            d_image: q.optionFiles?.[3],
            material_file: q.materialFile,
          }
        });
      });
    });

    console.log("[DEBUG] Final payload questions count:", allQuestions.length);
    if (allQuestions.length > 0) {
      console.log("[DEBUG] First question sample:", allQuestions[0]);
    }

    return allQuestions;
  };

  //  useEffect(() => {
  //   if (!editSectionsLoaded) {
  //     setGoClicked(false);
  //   }
  // }, [courseTitle, courseDesc, percentage, duration, noOfQuestions,editSectionsLoaded]);
  //    useEffect(() => {
  //   if (editSectionsLoaded && isFormFilled) {
  //     setGoClicked(true);
  //   }
  // }, [editSectionsLoaded, isFormFilled]);

  useEffect(() => {
    if (editSectionsLoaded && isFormFilled) {
      // Edit mode: always keep goClicked true, never reset it
      setGoClicked(true);
      return;
    }
    // New exam only: reset when form fields are cleared
    if (!editSectionsLoaded) {
      setGoClicked(false);
    }
  }, [courseTitle, courseDesc, percentage, duration, noOfQuestions, editSectionsLoaded, isFormFilled]);

  const buildFormData = (status) => {
    console.groupCollapsed(`%c Preparing FormData — status = ${status} (${status === 1 ? 'DRAFT' : status === 2 ? 'PUBLISHED' : 'UNKNOWN'})`, 'color: #0066cc; font-weight: bold;');
    const formData = new FormData();

    // 1. EXAM DATA
    const examPayload = {
      title: courseTitle,
      exampurpose:
        examPurpose === "Others"
          ? otherPurpose
          : examPurpose,
      description: courseDesc,
      pass_percent: Number(percentage),
      duration_min: Number(duration),
      exam_type_id: examTypeId,
      instructions: instructions,
      review_required: reviewRequired ? 1 : 0,
      review_with_answers: reviewWithAnswers ? 1 : 0,
      questions_per_user: Number(noOfQuestions),
      is_active: true,
      status: status,
      enable: 1,
      isenable: isenable,
      is_active: true,
      active: true,
      no_of_attempts: Number(maxAttempts),   // ✅ MUST BE HERE
    };


    console.log("Exam Payload:", examPayload);
    formData.append("exam", JSON.stringify(examPayload));

    // 2. QUESTIONS
    const questions = buildQuestionsPayload();

    // remove files from JSON
    const questionsWithoutFiles = questions.map(q => {
      const { _files, ...rest } = q;
      return rest;
    });

    console.log("Questions (without files):", questionsWithoutFiles);
    formData.append("questions", JSON.stringify(questionsWithoutFiles));

    console.log("Attached Files:");
    // 3. FILES (IMPORTANT PART)
    questions.forEach((q, index) => {
      if (!q._files) return;

      if (q._files.question_image) {
        formData.append(`question_image_${index}`, q._files.question_image);
      }

      if (q._files.a_image) {
        formData.append(`a_image_${index}`, q._files.a_image);
      }

      if (q._files.b_image) {
        formData.append(`b_image_${index}`, q._files.b_image);
      }

      if (q._files.c_image) {
        formData.append(`c_image_${index}`, q._files.c_image);
      }

      if (q._files.d_image) {
        formData.append(`d_image_${index}`, q._files.d_image);
      }

      if (q._files.material_file) {
        formData.append(`material_file_${index}`, q._files.material_file);
      }
    });

    return formData;
  };

  const prepareExamData = (status) => {
    const formData = buildFormData(status);
    return formData;
  };


  const handleConfirmSave = async (status) => {
    setIsSaving(true);
    setError(null);

    try {
      const hasPendingDeletes = deleteQueueRef.current.length > 0;
      // ✅ STEP 1: RUN DELETE APIs FIRST
      if (deleteQueueRef.current.length > 0) {


        for (const fn of deleteQueueRef.current) {
          try {
            await fn();
          } catch (err) {
            console.error("Delete API failed:", err);
          }
        }

        deleteQueueRef.current = [];
      }

      // ✅ STEP 2: THEN SAVE EXAM
      const isEdit =
        examid &&
        examid !== "0" &&
        examid !== "null" &&
        examid !== "" &&
        !isNaN(Number(examid)) &&
        Number(examid) > 0;

      const url = isEdit
        ? `/manager/theory_exam/${examid}/`
        : `/manager/theory_exams_create/`;

      const method = isEdit ? "put" : "post";

      const formData = prepareExamData(status);
      formData.append("has_pending_deletes", hasPendingDeletes ? "1" : "0");

      const res = await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" }
      });
      console.log(res, "mmmmmmmmmmmmmmmmmmmmmmmm");

      console.log(res, "SAVE RESPONSE");
      setLastSavedStatus(status);

      if (res.data?.success) {
        setFormChanged(false);
        console.log(successOpen, "lllllllllllllllllllll");
        setSuccessOpen(true);
        setConfirmationType("");
        setExam(prev => prev ? ({ ...prev, status }) : prev);
        setConfirmOpen(false);
        setError(null);
        // setLastSavedStatus(status);
      } else {
        console.log(successOpen, "kkkkkkkkkk");
        setConfirmOpen(false);
        // ✅ This shows the backend's validation message (e.g. "Question description is required...")
        setError(res.data?.message || "Save failed");
        setSuccessOpen(true);
      }

    } catch (err) {
      console.error("Save error:", err);
      setConfirmOpen(false);
      // ✅ KEY FIX: extract the backend message from the error response body
      const backendMessage =
        err.response?.data?.message ||   // { "message": "..." }
        err.response?.data?.error ||     // { "error": "..." }
        err.message ||                    // fallback: axios/network error
        "Failed to save assessment";

      setError(backendMessage);           // shows the real validation text
      setSuccessOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (permissionsLoading) return <div>Loading...</div>;



  if (!canAccessAssessment) {
    return <Navigate to="/error" replace />;
  }

  if (authLoading) return <MainLoadingComponent />;
  if (!canSeeActions) {
    return (
      <div className="flex min-h-screen">
        {/* <div className="w-[95px] flex-shrink-0"><Sidebar /></div> */}
        <div className="flex-1 overflow-auto pl-6 pr-6 pt-6">
          <div className="bgImage" style={{ paddingLeft: "100px", marginLeft: "-100px" }}>
            <Box className="MainSectionTop" sx={{ pl: 3, pr: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', maxWidth: '500px' }}>
                <div style={{ fontSize: '64px', color: '#dc3545', marginBottom: '20px' }}>⚠️</div>
                <h3 style={{ marginBottom: '15px', color: '#343a40' }}>Access Denied</h3>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>You don't have permission to access this page.</p>
                <button onClick={() => navigate('/managers/exams-list')} className="btn btn-primary">Go Back to Assessments List</button>
              </div>
            </Box>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !initialLoadComplete) return <MainLoadingComponent />;


  return (
    <div className="flex min-h-screen">
      {/* <div className="w-[95px] flex-shrink-0"><Sidebar /></div> */}
      <div className="flex-1 overflow-auto pl-6 pr-6 pt-6">

        {/* ─────────────────────────────────────────────────────────
            bgImage wrapper — position:relative so the overlay works
        ───────────────────────────────────────────────────────── */}
        <div className="bgImage" style={{ paddingLeft: "100px", marginLeft: "-100px", position: 'relative' }}>


          {/* ── END VIEW MODE OVERLAY ── */}

          <Box className="MainSectionTop" sx={{ pl: 3, pr: 3 }}>
            <div className="m-3">
              <h3 className="content-title">
                {courseType === "competency" ? "Competency Review Configurations" : "Objective Assessment Configurations"}
              </h3>
            </div>

            {error && (
              <div ref={errorRef} style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "12px 16px", borderRadius: "8px", margin: "0 20px 20px 20px" }}>
                {error}
              </div>
            )}

            {sectionError && (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  margin: "0 20px 20px 20px",
                  border: "1px solid #ffeeba"
                }}
              >
                {sectionError}
              </div>
            )}


            <div style={{ padding: "0 20px" }}>
              <div className="form-progress-wrapper" style={{ display: 'flex', gap: '32px', marginBottom: '32px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: isMobile ? '0 0 100%' : '0 0 50%', maxWidth: isMobile ? '100%' : '47.5%', minWidth: '320px' }}>
                  <Suspense fallback={<LoadingFallback />}>
                    <TheoryForm
                      courseTitle={courseTitle} setCourseTitle={setCourseTitle}
                      assessmentTypeId={assessmentTypeId} setAssessmentTypeId={setAssessmentTypeId}
                      examid={examid} examGenres={examGenres} loadingExamGenres={loadingExamGenres}
                      courseDesc={courseDesc} setCourseDesc={setCourseDesc}
                      percentage={percentage} setPercentage={setPercentage}
                      duration={duration} setDuration={setDuration}
                      difficulty={difficulty} setDifficulty={setDifficulty}
                      category={category} setCategory={setCategory}
                      categories={categories} difficulties={difficulties}
                      loading={loading} error={error} type={type}
                      reviewWithAnswers={reviewWithAnswers} setReviewWithAnswers={setReviewWithAnswers}
                      instructions={instructions} setInstructions={setInstructions}
                      onOpenInstructions={handleOpenInstructions} defaultInstructions={defaultInstructions}
                      permissionLevel={effectivePermissionLevel} isGeneratingQuestions={isGeneratingQuestions}
                      aiQuestionsGenerated={aiQuestionsGenerated.main} isFormFilled={isFormFilled}
                      numQuestions={noOfQuestions} setNumQuestions={setNoOfQuestions} examTypeId={examTypeId}
                      isRelative={isRelative} setIsRelative={setIsRelative}
                      isenable={isenable}
                      setIsEnable={setIsEnable}
                      goClicked={goClicked}
                      setGoClicked={setGoClicked}
                      examPurpose={examPurpose}
                      setExamPurpose={setExamPurpose}
                      examPurposes={examPurposes}
                      otherPurpose={otherPurpose}
                      setOtherPurpose={setOtherPurpose}
                      qaData={qaData}
                      sectionQuestions={sectionQuestions}
                      setPendingPurpose={setPendingPurpose}
                      setPurposeChangeDialogOpen={setPurposeChangeDialogOpen}
                      onAddSection={handleAddSection}
                      editSectionsLoaded={editSectionsLoaded}
                      setEditSectionsLoaded={setEditSectionsLoaded}
                      sections={sections}
                      formChanged={formChanged}
                      setFormChanged={setFormChanged}
                      maxAttempts={maxAttempts}
                      setMaxAttempts={setMaxAttempts}
                    />
                  </Suspense>
                </div>
                <div className="progress-column" style={{ flex: isMobile ? '0 0 100%' : '0 0 50%', maxWidth: isMobile ? '100%' : '50%', minWidth: '320px' }}>
                  <Suspense fallback={null}>
                    <TheoryFormProgressBar
                      courseTitle={courseTitle} courseDesc={courseDesc} percentage={percentage}
                      duration={duration} difficulty={difficulty} category={category}
                      difficulties={difficulties} categories={categories} loading={loading}
                      assessmentTypeId={assessmentTypeId} isFormFilled={isFormFilled} numQuestions={noOfQuestions} examTypeId={examTypeId}
                      examPurpose={examPurpose}
                      otherPurpose={otherPurpose}
                    />
                  </Suspense>


                </div>
              </div>

              {/* ── Sections list (no Add button here anymore) ── */}
              {isFormFilled && goClicked && (
                <div style={{ marginTop: '32px' }}>
                  {sections.map((section) => {
                    const isComplete =
                      section.genre &&
                      section.difficulty &&
                      section.category &&
                      !section.isEditing;
                    const showManualQA = section.category === "1";
                    const showObjectiveAI = isObjective && section.category === "3";
                    const showVivaAI = isViva && section.category === "3";
                    const aiData = getAiDataForSection(section.id);
                    const isCollapsed = collapsedSections[section.id] || false;

                    return (
                      <div key={section.id} style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '12px', padding: '24px', marginBottom: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'relative' }}>

                        {sectionErrors[section.id] && (
                          <div style={{ backgroundColor: "#fdecea", color: "#d32f2f", padding: "10px 14px", borderRadius: "6px", marginBottom: "10px", border: "1px solid #f5c6cb", fontSize: "14px", fontWeight: 500 }}>
                            {sectionErrors[section.id]}
                          </div>
                        )}

                        {/* Header with close and collapse buttons */}
                        <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '8px' }}>
                          {!isViewOnly && (
                            <>
                              <IconButton size="small" onClick={() => toggleCollapse(section.id)} sx={{ color: '#6b7280', '&:hover': { color: '#1976d2' } }}>
                                {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                              </IconButton>
                              <IconButton size="small" onClick={() => {
                                setSectionToDelete(section.id);
                                setSectionDeleteConfirmOpen(true);
                              }} sx={{ color: '#6b7280', '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)' } }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </div>


                        {/* <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px', fontSize: '1.05rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px', fontSize: '1.05rem', alignItems: 'center' }}>

                          <div><strong>Genre:</strong> {examGenres.find(g => String(g.Id) === section.genre)?.Title || section.genre}</div>
                          <div><strong>Difficulty:</strong> {difficultyLabelMap[section.difficulty] || section.difficulty}</div>
                          <div><strong>Category:</strong> {categories?.find(c => String(c.Id) === section.category)?.Type || section.category}</div>
                          {!section.isEditing && (
                            <IconButton size="small" onClick={() => editSection(section.id)} sx={{ color: '#1976d2' }}><EditIcon fontSize="small" /></IconButton>
                          )}
                        </div> */}

                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '24px',
                            marginBottom: '24px',
                            fontSize: '1.05rem'
                          }}
                        >
                          <div>
                            <strong>Genre:</strong> {examGenres.find(g => String(g.Id) === section.genre)?.Title || section.genre}
                          </div>

                          <div>
                            <strong>Difficulty:</strong> {difficultyLabelMap[section.difficulty] || section.difficulty}
                          </div>

                          <div>
                            <strong>Category:</strong> {categories?.find(c => String(c.Id) === section.category)?.Type || section.category}
                          </div>

                          {!section.isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => editSection(section.id)}
                              sx={{
                                color: '#1976d2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </div>

                        {/* Content (collapsible) */}
                        {!isCollapsed && (
                          <div style={{ paddingRight: '48px' }}>
                            {isComplete ? (
                              <>
                                {section.category === "3" ? (
                                  <>
                                    {!aiQuestionsGenerated[section.id] ? (
                                      <TheoryAIQuestionSection
                                        sectionId={section.id}
                                        aiDomain={aiData.domain}
                                        setAiDomain={(val) => updateAiSectionData(section.id, 'domain', val)}
                                        aiTopic={aiData.topic}
                                        setAiTopic={(val) => updateAiSectionData(section.id, 'topic', val)}
                                        aiSubtopic={aiData.subtopic}
                                        setAiSubtopic={(val) => updateAiSectionData(section.id, 'subtopic', val)}
                                        assessmentType={section.genre}
                                        aiQuestionTypes={aiData.questionTypes}
                                        setAiQuestionTypes={(val) => updateAiSectionData(section.id, 'questionTypes', val)}
                                        aiQuestionCount={aiData.questionCount}
                                        setAiQuestionCount={(val) => updateAiSectionData(section.id, 'questionCount', val)}
                                        aiMaterialFile={aiData.materialFile}
                                        setAiMaterialFile={(val) => updateAiSectionData(section.id, 'materialFile', val)}
                                        aiQuestionsGenerated={!!aiQuestionsGenerated[section.id]}
                                        setAiQuestionsGenerated={(val) => setAiQuestionsGenerated(prev => ({ ...prev, [section.id]: val }))}
                                        sectionQuestions={sectionQuestions}
                                        setSectionQuestions={setSectionQuestions}
                                        questionTypes={questionTypes}
                                        difficulty={section.difficulty}
                                        type={type}
                                        editaimaterial={aiData.materialPath}
                                        collapsed={aiSectionCollapsed}
                                        setCollapsed={setAiSectionCollapsed}
                                        permissionLevel={effectivePermissionLevel}
                                        onGeneratingStateChange={setIsGeneratingQuestions}
                                        examTypeId={examTypeId}
                                        setError={setError}
                                        examPurpose={examPurpose}
                                        otherPurpose={otherPurpose}
                                      />
                                    ) : (
                                      examTypeId === 1 ? (
                                        <div style={{ display: 'flex', gap: '32px', flexDirection: isDesktop ? 'row' : 'column' }}>
                                          <div style={{ flex: 1, minWidth: isDesktop ? '49.3%' : '100%' }}>
                                            <QuestionList
                                              sectionId={section.id}
                                              sectionQuestions={sectionQuestions}
                                              setSectionQuestions={setSectionQuestions}
                                              difficulty={section.difficulty}
                                              questionTypes={questionTypes}
                                              difficulties={difficulties}
                                              showQA={true}
                                              showAISection={true}
                                              aiQuestionsGenerated={aiQuestionsGenerated[section.id]}
                                              currentPage={sectionPages[section.id] || 1}
                                              setCurrentPage={(page) => setSectionPages(prev => ({ ...prev, [section.id]: page }))}
                                              isFormFilled={isFormFilled}
                                              confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen}
                                              confirmationType={confirmationType} setConfirmationType={setConfirmationType}
                                              successOpen={successOpen} setSuccessOpen={setSuccessOpen}
                                              isSaving={isSaving} setIsSaving={setIsSaving}
                                              resetCourseForm={resetCourseForm} handleSuccessClose={handleSuccessClose}
                                              courseTitle={courseTitle} courseDesc={courseDesc} percentage={percentage}
                                              duration={duration} category={category}
                                              aiDomain={aiData.domain} aiTopic={aiData.topic} aiSubtopic={aiData.subtopic} aiMaterialFile={aiData.materialFile}
                                              type={type} examid={examid} examStatus={examStatus}
                                              instructions={instructions} permissionLevel={effectivePermissionLevel}
                                              hideActionButtons={true} examTypeId={examTypeId}
                                              queueDeleteAction={queueDeleteAction}
                                              removeSection={removeSection}
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: isDesktop ? '52%' : '100%' }}>

                                            <TheoryAIQuestionSection
                                              sectionId={section.id}
                                              aiDomain={aiData.domain}
                                              setAiDomain={(val) => updateAiSectionData(section.id, 'domain', val)}
                                              aiTopic={aiData.topic}
                                              setAiTopic={(val) => updateAiSectionData(section.id, 'topic', val)}
                                              aiSubtopic={aiData.subtopic}
                                              setAiSubtopic={(val) => updateAiSectionData(section.id, 'subtopic', val)}
                                              assessmentType={section.genre}
                                              aiQuestionTypes={aiData.questionTypes}
                                              setAiQuestionTypes={(val) => updateAiSectionData(section.id, 'questionTypes', val)}
                                              aiQuestionCount={aiData.questionCount}
                                              setAiQuestionCount={(val) => updateAiSectionData(section.id, 'questionCount', val)}
                                              aiMaterialFile={aiData.materialFile}
                                              setAiMaterialFile={(val) => updateAiSectionData(section.id, 'materialFile', val)}
                                              aiQuestionsGenerated={!!aiQuestionsGenerated[section.id]}
                                              setAiQuestionsGenerated={(val) => setAiQuestionsGenerated(prev => ({ ...prev, [section.id]: val }))}
                                              sectionQuestions={sectionQuestions}
                                              setSectionQuestions={setSectionQuestions}
                                              questionTypes={questionTypes}
                                              difficulty={section.difficulty}
                                              type={type}
                                              editaimaterial={aiData.materialPath}
                                              collapsed={aiSectionCollapsed}
                                              setCollapsed={setAiSectionCollapsed}
                                              permissionLevel={effectivePermissionLevel}
                                              onGeneratingStateChange={setIsGeneratingQuestions}
                                              examTypeId={examTypeId}
                                              setError={setError}
                                              examPurpose={examPurpose}
                                              otherPurpose={otherPurpose}
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', gap: '32px', flexDirection: isDesktop ? 'row' : 'column' }}>
                                          <div style={{ flex: 1, minWidth: isDesktop ? '49.3%' : '100%' }}>
                                            <QuestionList
                                              sectionId={section.id}
                                              sectionQuestions={sectionQuestions}
                                              setSectionQuestions={setSectionQuestions}
                                              difficulty={section.difficulty}
                                              questionTypes={questionTypes}
                                              difficulties={difficulties}
                                              showQA={true}
                                              showAISection={true}
                                              aiQuestionsGenerated={aiQuestionsGenerated[section.id]}
                                              currentPage={sectionPages[section.id] || 1}
                                              setCurrentPage={(page) => setSectionPages(prev => ({ ...prev, [section.id]: page }))}
                                              isFormFilled={isFormFilled}
                                              confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen}
                                              confirmationType={confirmationType} setConfirmationType={setConfirmationType}
                                              successOpen={successOpen} setSuccessOpen={setSuccessOpen}
                                              isSaving={isSaving} setIsSaving={setIsSaving}
                                              resetCourseForm={resetCourseForm} handleSuccessClose={handleSuccessClose}
                                              courseTitle={courseTitle} courseDesc={courseDesc} percentage={percentage}
                                              duration={duration} category={category}
                                              aiDomain={aiData.domain} aiTopic={aiData.topic} aiSubtopic={aiData.subtopic} aiMaterialFile={aiData.materialFile}
                                              type={type} examid={examid} examStatus={examStatus}
                                              instructions={instructions} permissionLevel={effectivePermissionLevel}
                                              hideActionButtons={true} examTypeId={examTypeId}
                                              isVivaMode={true} isAiSection={true}
                                              queueDeleteAction={queueDeleteAction}
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: isDesktop ? '52%' : '100%' }}>

                                            <TheoryAIQuestionSection
                                              sectionId={section.id}
                                              aiDomain={aiData.domain}
                                              setAiDomain={(val) => updateAiSectionData(section.id, 'domain', val)}
                                              aiTopic={aiData.topic}
                                              setAiTopic={(val) => updateAiSectionData(section.id, 'topic', val)}
                                              aiSubtopic={aiData.subtopic}
                                              setAiSubtopic={(val) => updateAiSectionData(section.id, 'subtopic', val)}
                                              assessmentType={section.genre}
                                              aiQuestionTypes={aiData.questionTypes}
                                              setAiQuestionTypes={(val) => updateAiSectionData(section.id, 'questionTypes', val)}
                                              aiQuestionCount={aiData.questionCount}
                                              setAiQuestionCount={(val) => updateAiSectionData(section.id, 'questionCount', val)}
                                              aiMaterialFile={aiData.materialFile}
                                              setAiMaterialFile={(val) => updateAiSectionData(section.id, 'materialFile', val)}
                                              aiQuestionsGenerated={!!aiQuestionsGenerated[section.id]}
                                              setAiQuestionsGenerated={(val) => setAiQuestionsGenerated(prev => ({ ...prev, [section.id]: val }))}
                                              sectionQuestions={sectionQuestions}
                                              setSectionQuestions={setSectionQuestions}
                                              questionTypes={questionTypes}
                                              difficulty={section.difficulty}
                                              type={type}
                                              editaimaterial={aiData.materialPath}
                                              collapsed={aiSectionCollapsed}
                                              setCollapsed={setAiSectionCollapsed}
                                              permissionLevel={effectivePermissionLevel}
                                              onGeneratingStateChange={setIsGeneratingQuestions}
                                              examTypeId={examTypeId}
                                              setError={setError}
                                              isVivaMode={true}
                                              examPurpose={examPurpose}
                                              otherPurpose={otherPurpose}
                                            />
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </>
                                ) : (
                                  <QuestionList
                                    sectionId={section.id}
                                    sectionQuestions={sectionQuestions}
                                    setSectionQuestions={setSectionQuestions}
                                    difficulty={section.difficulty}
                                    questionTypes={questionTypes}
                                    difficulties={difficulties}
                                    showQA={true}
                                    showAISection={false}
                                    aiQuestionsGenerated={false}
                                    currentPage={sectionPages[section.id] || 1}
                                    setCurrentPage={(page) => setSectionPages(prev => ({ ...prev, [section.id]: page }))}
                                    isFormFilled={isFormFilled}
                                    confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen}
                                    confirmationType={confirmationType} setConfirmationType={setConfirmationType}
                                    successOpen={successOpen} setSuccessOpen={setSuccessOpen}
                                    isSaving={isSaving} setIsSaving={setIsSaving}
                                    resetCourseForm={resetCourseForm} handleSuccessClose={handleSuccessClose}
                                    courseTitle={courseTitle} courseDesc={courseDesc} percentage={percentage}
                                    duration={duration} category={category}
                                    aiDomain={aiDomain} aiTopic={aiTopic} aiSubtopic={aiSubtopic} aiMaterialFile={aiMaterialFile}
                                    type={type} examid={examid} examStatus={examStatus}
                                    instructions={instructions} permissionLevel={effectivePermissionLevel}
                                    hideActionButtons={true} examTypeId={examTypeId}
                                    queueDeleteAction={queueDeleteAction}
                                  />
                                )}
                              </>
                            ) : (
                              // Dropdowns for incomplete section
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
                                <div>
                                  <label style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                    Assessment Genre <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <select
                                    value={section.genre || ""}
                                    onChange={(e) => updateSection(section.id, { genre: e.target.value })}
                                    disabled={isViewOnly || loadingExamGenres}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white' }}
                                  >
                                    <option value="">Select Genre</option>
                                    {examGenres.map(genre => (
                                      <option key={genre.Id} value={String(genre.Id)}>
                                        {genre.Title || genre.name || `Genre ${genre.Id}`}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div>
                                  <label style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                    Difficulty <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <select
                                    value={section.difficulty || ""}
                                    onChange={(e) => updateSection(section.id, { difficulty: e.target.value })}
                                    disabled={isViewOnly}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white' }}
                                  >
                                    <option value="">Select Difficulty</option>
                                    {difficulties?.map(d => (
                                      <option key={d.ID} value={String(d.ID)}>
                                        {d.Level || difficultyLabelMap[String(d.ID)] || `Level ${d.ID}`}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div>
                                  <label style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                    Category <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <select
                                    value={section.category || ""}
                                    onChange={(e) => updateSection(section.id, { category: e.target.value })}
                                    disabled={isViewOnly}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white' }}
                                  >
                                    <option value="">Select Category</option>
                                    {categories?.map(cat => (
                                      <option key={cat.Id} value={String(cat.Id)}>
                                        {cat.Type || cat.name || `Category ${cat.Id}`}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}


              <div className="question-list-save-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '10px 12px 8px', borderTop: '1px solid #eee', backgroundColor: '#fafafa', marginTop: '20px', flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', paddingBottom: '12px' }}>

                {mode === 'view' ? (
                  /* ── View mode: replace all buttons with a single notice ── */
                  <div style={{
                    width: '100%', textAlign: 'center', padding: '8px 16px',
                    backgroundColor: '#fff8e1', border: '1px solid #f59e0b',
                    borderRadius: '6px', color: '#92400e', fontSize: '13px', fontWeight: 500,
                  }}>
                    🔒 View-only mode
                  </div>
                ) : (
                  /* ── Normal / edit mode: original buttons ── */
                  <>
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                      <button type="button" onClick={() => setReviewOpen(true)} className="btn" disabled={!hasQuestionsDisplayed || isViewOnly}
                        // style={{ opacity: isViewOnly ? 0.6 : 1, cursor: isViewOnly ? 'not-allowed' : 'pointer', flexShrink: 0, whiteSpace: 'nowrap', minWidth: '70px' }}>

                        style={{
                          backgroundColor:
                            (!hasQuestionsDisplayed || isViewOnly)
                              ? "#d3d3d3"
                              : "#1976d2",

                          border:
                            (!hasQuestionsDisplayed || isViewOnly)
                              ? "1px solid #d3d3d3"
                              : "1px solid #1976d2",

                          color:
                            (!hasQuestionsDisplayed || isViewOnly)
                              ? "#666"
                              : "#fff",

                          cursor:
                            (!hasQuestionsDisplayed || isViewOnly)
                              ? "not-allowed"
                              : "pointer",

                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          minWidth: "70px"
                        }}
                      >
                        <i className="fa fa-eye icon"></i>
                        <span className="d-none d-md-inline">Review Questions</span>
                        <span className="d-md-none"></span>
                      </button>
                      {exam?.status !== 1 && (
                        <button type="button" onClick={handleSaveDraft} className="btn" disabled={!canSaveAsDraft || isSaving || isViewOnly} style={{ opacity: (!canSaveAsDraft || isSaving || isViewOnly) ? 0.6 : 1, cursor: (!canSaveAsDraft || isSaving || isViewOnly) ? 'not-allowed' : 'pointer', flexShrink: 0, whiteSpace: 'nowrap', minWidth: '70px' }}>
                          <i className="fa fa-save icon"></i>
                          <span className="d-none d-md-inline">Save as Draft</span>
                          <span className="d-md-none">Draft</span>
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: '15px' }}>
                      <button type="button" className="btn" onClick={handleSaveExam} disabled={isSaving || !isFormFilled || isViewOnly || !hasQuestionsDisplayed}
                        // style={{ opacity: isViewOnly ? 0.6 : 1, cursor: isViewOnly ? 'not-allowed' : 'pointer', flexShrink: 0, whiteSpace: 'nowrap', minWidth: '80px' }}>
                        style={{
                          backgroundColor:
                            (isSaving || !isFormFilled || isViewOnly || !hasQuestionsDisplayed)
                              ? "#d3d3d3"
                              : "#1976d2",

                          border:
                            (isSaving || !isFormFilled || isViewOnly || !hasQuestionsDisplayed)
                              ? "1px solid #d3d3d3"
                              : "1px solid #1976d2",

                          color:
                            (isSaving || !isFormFilled || isViewOnly || !hasQuestionsDisplayed)
                              ? "#666"
                              : "#fff",

                          cursor:
                            (isSaving || !isFormFilled || isViewOnly || !hasQuestionsDisplayed)
                              ? "not-allowed"
                              : "pointer",

                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          minWidth: "80px"
                        }}
                      >


                        <i className="fa fa-check icon"></i>
                        <span className="d-none d-md-inline">{isSaving ? "Saving..." : "Save Assessment"}</span>
                        <span className="d-md-none">{isSaving ? "..." : "Save"}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
              {/* ── END SAVE BAR ── */}

            </div>

            {/* Review Dialog */}
            {reviewOpen && (
              <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth={isMobile ? "sm" : "lg"} fullWidth fullScreen={isMobile} scroll="paper" sx={{ '& .MuiDialog-paper': { borderRadius: isMobile ? 0 : '12px', overflow: 'hidden', margin: isMobile ? 0 : '32px', maxHeight: isMobile ? '100%' : 'calc(100% - 64px)' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E7E8E9', padding: isMobile ? '12px 16px' : '16px 24px', backgroundColor: '#f8f9fa' }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, color: '#374151' }}>Review All Questions</Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: isMobile ? '16px' : '24px', maxHeight: isMobile ? 'calc(100vh - 130px)' : 'calc(80vh - 130px)' }}>
                  <Box sx={{ padding: isMobile ? 0 : '8px' }}>
                    {/* {allPreviewQuestions.map((question, index) => (
                      <Box key={index} sx={{ marginBottom: '24px', padding: isMobile ? '16px' : '20px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                        <Typography variant={isMobile ? "body2" : "subtitle1"} fontWeight={600} sx={{ color: '#1976d2', mb: 1 }}>
                          {question.sectionId === 'main' ? 'Main' : `Section ${question.sectionId}`} – Question {index + 1} ({difficultyLabelMap[question.difficulty] || "Unknown"}): {question.topic || 'No Topic'}
                        </Typography>
                        <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 2 }}>{question.question}</Typography>
                        {(question.type === "1" || question.type === "2") && question.options && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: isMobile ? '0.875rem' : '1rem' }}>Options:</Typography>
                            {question.options.map((opt, optIdx) => (
                              <Typography key={optIdx} variant="body2" sx={{ mb: 0.5, fontSize: isMobile ? '0.875rem' : '1rem', color: (question.type === "1" && question.correctAnswer === opt) || (question.type === "2" && Array.isArray(question.correctAnswer) && question.correctAnswer.includes(opt)) ? '#2e7d32' : 'inherit', fontWeight: (question.type === "1" && question.correctAnswer === opt) || (question.type === "2" && Array.isArray(question.correctAnswer) && question.correctAnswer.includes(opt)) ? 600 : 'normal' }}>
                                {String.fromCharCode(65 + optIdx)}. {opt}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {question.type === "3" && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, fontSize: isMobile ? '0.875rem' : '1rem' }}>Correct Answer:</Typography>
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: isMobile ? '0.875rem' : '1rem' }}>{question.correctAnswer}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Type: {questionTypes?.find(qt => qt.ID.toString() === question.type)?.Type || question.type}</Typography>
                          {question.isMandatory && <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Mandatory</Typography>}
                        </Box>
                      </Box>
                    ))} */}

                    {allPreviewQuestions.map((question, index) => {
                      // Determine section display name
                      // let sectionDisplay = "Main Section";

                      // if (question.sectionId !== 'main') {
                      //   const section = sections.find(s => s.id === question.sectionId);
                      //   sectionDisplay = section?.name
                      //     ? `Section ${section.name}`
                      //     : `Section ${question.sectionId.slice(0, 8)}...`; // fallback for safety
                      // }

                      return (
                        <Box
                          key={index}
                          sx={{
                            marginBottom: '24px',
                            padding: isMobile ? '16px' : '20px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa'
                          }}
                        >
                          <Typography
                            variant={isMobile ? "body2" : "subtitle1"}
                            fontWeight={600}
                            sx={{ color: '#1976d2', mb: 1 }}
                          >
                            Question {index + 1} (
                            {difficultyLabelMap[question.difficulty] || "Unknown"}
                            ): {question.topic || 'No Topic'}
                          </Typography>

                          <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 2 }}>
                            {question.question}
                          </Typography>

                          {/* Question Image - only for Objective assessments */}
                          {examTypeId === 1 && (tempQuestionUrls[index] || question.questionImagePath) && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                                Question Image:
                              </Typography>
                              <img
                                src={tempQuestionUrls[index] || question.questionImagePath}
                                alt="Question"
                                style={{ maxWidth: '50%', maxHeight: '220px', objectFit: 'contain', border: '1px solid #ccc', borderRadius: '4px' }}
                              />
                            </Box>
                          )}

                          {/* {(question.type === "1" || question.type === "2") && question.options && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ mb: 1, fontSize: isMobile ? '0.875rem' : '1rem' }}
                              >
                                Options:
                              </Typography>
                              {question.options.map((opt, optIdx) => (
                                <Typography
                                  key={optIdx}
                                  variant="body2"
                                  sx={{
                                    mb: 0.5,
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    color: (question.type === "1" && question.correctAnswer === opt) ||
                                      (question.type === "2" && Array.isArray(question.correctAnswer) &&
                                        question.correctAnswer.includes(opt))
                                      ? '#2e7d32'
                                      : 'inherit',
                                    fontWeight: (question.type === "1" && question.correctAnswer === opt) ||
                                      (question.type === "2" && Array.isArray(question.correctAnswer) &&
                                        question.correctAnswer.includes(opt))
                                      ? 600
                                      : 'normal'
                                  }}
                                >
                                  {String.fromCharCode(65 + optIdx)}. {opt}
                                </Typography>
                              ))}
                            </Box>
                          )} */}
                          {/* Show options only for Objective assessments (examTypeId === 1) */}
                          {examTypeId === 1 && (question.type === "1" || question.type === "2") && question.options && (
                            <>
                              {question.options.map((opt, optIdx) => {
                                const isCorrect =
                                  question.type === "1"
                                    ? question.correctAnswer === optIdx
                                    : Array.isArray(question.correctAnswer) && question.correctAnswer.includes(optIdx);

                                let imgSrc = null;
                                const fileKey = `${index}-${optIdx}`;
                                if (tempOptionUrls[fileKey]) {
                                  imgSrc = tempOptionUrls[fileKey];
                                } else if (question.optionPaths && question.optionPaths[optIdx]) {
                                  imgSrc = question.optionPaths[optIdx];
                                }

                                return (
                                  <Box key={optIdx} sx={{ mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: isMobile ? '0.875rem' : '1rem',
                                        color: isCorrect ? '#2e7d32' : 'inherit',
                                        fontWeight: isCorrect ? 600 : 'normal',
                                        backgroundColor: isCorrect ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
                                        padding: isCorrect ? '2px 6px' : 0,
                                        borderRadius: '4px',
                                        display: 'inline-block',
                                      }}
                                    >
                                      {String.fromCharCode(65 + optIdx)}. {opt}
                                      {isCorrect && (
                                        <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.9 }}>
                                          ✓ Correct
                                        </span>
                                      )}
                                    </Typography>
                                    {imgSrc && (
                                      <Box sx={{ mt: 1 }}>
                                        <img
                                          src={imgSrc}
                                          alt={`Option ${String.fromCharCode(65 + optIdx)}`}
                                          width="220"
                                          height="220"
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </>
                          )}

                          {/* For Competency (examTypeId === 2) or Viva questions, show Expected Answer */}
                          {(examTypeId === 2 || question.type === "3" || question.type === "4") && question.correctAnswer && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ mb: 0.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                              >
                                Expected Answer:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: '#2e7d32', fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem' }}
                              >
                                {question.correctAnswer}
                              </Typography>
                            </Box>
                          )}

                          {
                            question.type === "3" && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  sx={{ mb: 0.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                                >
                                  Correct Answer:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#2e7d32', fontWeight: 600, fontSize: isMobile ? '0.875rem' : '1rem' }}
                                >
                                  {question.correctAnswer}
                                </Typography>
                              </Box>
                            )
                          }

                          < Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              Type: {questionTypes?.find(qt => qt.ID.toString() === question.type)?.Type || question.type}
                            </Typography>
                            {question.isMandatory && (
                              <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                Mandatory
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </DialogContent>
                <DialogActions sx={{ padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #E7E8E9', backgroundColor: '#f8f9fa' }}>
                  <Button onClick={() => setReviewOpen(false)} className="btn-negative" sx={{ textTransform: 'none', fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem', padding: isMobile ? '8px 16px' : '10px 20px' }}>Close Review</Button>
                </DialogActions>
              </Dialog>
            )}

            <Dialog
              open={sectionDeleteConfirmOpen}
              onClose={() => {
                setSectionDeleteConfirmOpen(false);
                setSectionToDelete(null);
              }}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                padding: '16px 24px'
              }}>
                <Typography variant="h6">Delete Section?</Typography>
                <IconButton
                  onClick={() => {
                    setSectionDeleteConfirmOpen(false);
                    setSectionToDelete(null);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ py: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to delete this section?
                  </Typography>
                  <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                    All questions in this section will be permanently deleted.<br />
                    This action cannot be undone.
                  </Typography>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  className="btn-negative"
                  onClick={() => {
                    setSectionDeleteConfirmOpen(false);
                    setSectionToDelete(null);
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  className="btn"
                  onClick={() => {
                    if (sectionToDelete) {
                      removeSection(sectionToDelete);
                    }
                    setSectionDeleteConfirmOpen(false);
                    setSectionToDelete(null);
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? "Deleting..." : "Delete Section"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={purposeChangeDialogOpen}
              onClose={() => {
                setPurposeChangeDialogOpen(false);
                setPendingPurpose("");
              }}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #e0e0e0",
                  padding: "16px 24px",
                }}
              >
                <Typography variant="h6">
                  Change Assessment Purpose?
                </Typography>

                <IconButton
                  onClick={() => {
                    setPurposeChangeDialogOpen(false);
                    setPendingPurpose("");
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ py: 4 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Changing the Assessment Purpose will delete all existing questions.
                  </Typography>

                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ fontWeight: 500 }}
                  >
                    All generated and manually added questions will be permanently deleted.
                    <br />
                    This action cannot be undone.
                  </Typography>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  className="btn-negative"
                  onClick={() => {
                    setPurposeChangeDialogOpen(false);
                    setPendingPurpose("");
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>

                <Button
                  className="btn"
                  onClick={handlePurposeChangeConfirm}
                  disabled={isSaving}
                >
                  {isSaving ? "Deleting..." : "Delete & Continue"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Instructions Dialog */}
            {canUseActions && (
              <Suspense>
                <InstructionsDialog open={instructionsDialogOpen} onClose={() => setInstructionsDialogOpen(false)} onSave={handleInstructionsSave} initialValue={instructions} permissionLevel={effectivePermissionLevel} />
              </Suspense>
            )}

            {/* Dialogs */}
            <Suspense>
              <Dialogs
                confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen} confirmationType={confirmationType}
                assessmentTypeId={assessmentTypeId} setAssessmentTypeId={setAssessmentTypeId}
                setConfirmationType={setConfirmationType} viewFileOpen={viewFileOpen} setViewFileOpen={setViewFileOpen}
                viewFile={viewFile} setViewFile={setViewFile} successOpen={successOpen} setSuccessOpen={setSuccessOpen}
                courseTitle={courseTitle} courseDesc={courseDesc} percentage={percentage} duration={duration}
                category={category} difficulty={difficulty} aiDomain={aiDomain} aiTopic={aiTopic} aiSubtopic={aiSubtopic}
                aiMaterialFile={aiMaterialFile} qaData={qaData} setQaData={setQaData} questionTypes={questionTypes}
                type={type} examid={examid} isSaving={isSaving} setIsSaving={setIsSaving} resetCourseForm={resetCourseForm}
                aiQuestionsGenerated={aiQuestionsGenerated.main} reviewRequired={reviewRequired} reviewWithAnswers={reviewWithAnswers}
                instructions={instructions} examStatus={examStatus} handleSuccessClose={handleSuccessClose}
                onConfirmClear={handleConfirmClear} setError={setError} saveSummary={saveSummary} setSaveSummary={setSaveSummary}
                getFormData={prepareExamData} onConfirmSave={handleConfirmSave} examTypeId={examTypeId} error={error}
              />
            </Suspense>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default CreateCourse;
